/**
 * P2 (AN-PP2 §5.3) — formas de resposta de oportunidades para a elegibilidade da reserva
 * (snapshot, decisões, rota e fila de Grandes Operações).
 *
 * Espelha os DTOs REAIS de originei-oportunidades (`ElegibilidadeReservaResponse`,
 * `AvaliarElegibilidadeResponse`, `FilaGrandesOperacoesResponse`) — conferido na integração da P2.
 * O backend serializa com `non_null`: campo nulo é OMITIDO, por isso os opcionais (`?`) e a leitura
 * com `== null`.
 */
import type {
  AvaliacaoElegibilidade,
  ExplicacaoElegibilidade,
  FonteValorImovel,
  MomentoAvaliacaoElegibilidade,
  OrigemFaturamento,
  OrigemRota,
  ResultadoElegibilidade,
  RotaOperacao,
  TipoDecisaoElegibilidade,
  TipoElegibilidade,
} from './elegibilidade'
import type { EtapaComercial, StatusReserva } from './statusReserva'

/** Uma linha de `avaliacoes_elegibilidade_reserva` (snapshot append-only). */
export interface AvaliacaoElegibilidadeReserva extends AvaliacaoElegibilidade {
  id: string
  /** Versão das entradas declaradas (Q69). */
  sequencia: number
  momento: MomentoAvaliacaoElegibilidade
  produtoId?: string | null
  produtoCodigo?: string | null
  politicaId?: string | null
  politicaVersao?: number | null
  politicaTipo?: TipoElegibilidade | null
  /** ISO `YYYY-MM-DD` (effectiveAt). */
  politicaVigenciaInicio?: string | null
  faturamentoAnual?: number | null
  faturamentoOrigem?: OrigemFaturamento | null
  /** ISO date-time — "data" do faturamento declarado (Q69). */
  faturamentoDeclaradoEm?: string | null
  /** Omitido para o consultor quando não foi ele quem declarou. */
  faturamentoDeclaradoPor?: string | null
  valorOperacao?: number | null
  valorImovel?: number | null
  valorImovelFonte?: FonteValorImovel | null
  valorSolicitado?: number | null
  /** Sugestão do motor; a decisão gravada é `ElegibilidadeReserva.rota.rotaOperacao`. */
  rotaCalculada?: RotaOperacao | null
  hashEntradas?: string | null
  /** Omitido para o consultor quando não foi ele quem avaliou. */
  avaliadoPor?: string | null
  /** ISO date-time. */
  avaliadoEm: string
}

/** Uma linha de `decisoes_elegibilidade_reserva`. Consultor recebe sem `autorId/autorNome`. */
export interface DecisaoElegibilidadeReserva {
  id: string
  avaliacaoId?: string | null
  tipo: TipoDecisaoElegibilidade
  rotaAnterior?: RotaOperacao | null
  rotaNova?: RotaOperacao | null
  motivo: string
  autorId?: string | null
  autorNome?: string | null
  /** ISO date-time. */
  ocorridoEm: string
  /** A sobreposição ainda vale (mesma política e entradas) / a rota ainda é a gravada. */
  vigente: boolean
}

/**
 * Avaliação AO VIVO (política vigente, não persistida) — o que o Encaminhamento faria agora.
 * `politicaId`/`hashEntradas` são o que o ADMIN sobrepõe (409 `AVALIACAO_MUDOU` se mudarem).
 */
export interface SituacaoEncaminhamentoElegibilidade {
  resultado?: ResultadoElegibilidade | null
  /**
   * O RESULTADO do motor impede o Encaminhamento (`NAO_ATENDE | DADOS_INSUFICIENTES | SEM_POLITICA`) —
   * mesmo que o ADMIN já tenha sobreposto. Não é "bloqueado agora": para isso use `exigeSobreposicao`.
   */
  impede: boolean
  /** Impede E não há sobreposição válida: o Encaminhamento seria recusado agora (422). */
  exigeSobreposicao: boolean
  /** Sobreposição do ADMIN que libera o Encaminhamento (omitida quando não há). */
  sobreposicaoId?: string | null
  divergeDaUltima: boolean
  politicaId?: string | null
  politicaVersao?: number | null
  politicaTipo?: TipoElegibilidade | null
  hashEntradas?: string | null
  rotaCalculada?: RotaOperacao | null
  ltvCalculado?: number | null
  explicacoes?: ExplicacaoElegibilidade[] | null
}

/**
 * Dados declarados vigentes (Q69) — os da última avaliação. `sequenciaAvaliacao` é a `sequencia` do
 * snapshot de onde vieram (0 = nenhuma avaliação ainda) — conta toda avaliação gravada, não só
 * mudança de dado declarado. `valorSolicitado` já vem efetivo (sem declaração = valor da operação,
 * `valorSolicitadoDerivado`).
 */
export interface DadosDeclaradosReserva {
  sequenciaAvaliacao: number
  faturamentoAnual?: number | null
  faturamentoOrigem?: OrigemFaturamento | null
  /** ISO date-time. */
  faturamentoDeclaradoEm?: string | null
  valorOperacao?: number | null
  valorImovel?: number | null
  valorImovelFonte?: FonteValorImovel | null
  valorSolicitado?: number | null
  valorSolicitadoDerivado: boolean
}

/** Rota gravada da reserva (Q67). Os campos ficam omitidos em produto sem rota (não `RECEIVABLES_RANGE`). */
export interface RotaReserva {
  rotaOperacao?: RotaOperacao | null
  rotaOrigem?: OrigemRota | null
  /** ISO date-time. */
  rotaDefinidaEm?: string | null
  /** O que a política vigente calcularia agora. */
  rotaCalculada?: RotaOperacao | null
  /** A rota gravada (ex.: definida pelo ADMIN) difere da calculada. */
  divergeDaCalculada: boolean
}

/** `GET /v1/oportunidades/{id}/elegibilidade` (e resposta de PUT/POST da elegibilidade). */
export interface ElegibilidadeReserva {
  oportunidadeId: string
  /** "Elegibilidade não equivale a aprovação de crédito." */
  aviso?: string | null
  /** Omitida na reserva legada sem snapshot (sem backfill — §5.1). */
  ultima?: AvaliacaoElegibilidadeReserva | null
  /** Mais recente primeiro. */
  historico?: AvaliacaoElegibilidadeReserva[] | null
  dadosDeclarados?: DadosDeclaradosReserva | null
  /** O servidor diz se quem pede pode editar os dados declarados agora. */
  dadosEditaveis: boolean
  rota?: RotaReserva | null
  decisoes?: DecisaoElegibilidadeReserva[] | null
  situacaoEncaminhamento?: SituacaoEncaminhamentoElegibilidade | null
}

/**
 * `PUT /v1/oportunidades/{id}/elegibilidade/dados` — SEMÂNTICA DE SUBSTITUIÇÃO: o corpo é o conjunto
 * inteiro (faturamento, imóvel, fonte, solicitado); campo ausente vira nulo no servidor. Sempre mandar
 * os quatro, nunca só o editado.
 */
export interface AtualizarDadosElegibilidadeRequest {
  faturamentoAnual: number | null
  valorImovel: number | null
  valorImovelFonte: FonteValorImovel | null
  valorSolicitado: number | null
}

/** `POST /v1/oportunidades/{id}/elegibilidade/sobreposicoes` (ADMIN). */
export interface SobreporImpedimentoRequest {
  motivo: string
  politicaId?: string | null
  hashEntradas?: string | null
}

/** `POST /v1/oportunidades/{id}/rota` (ADMIN). */
export interface AlterarRotaRequest {
  rota: RotaOperacao
  motivo: string
}

/** Corpo `data` do 422 `ELEGIBILIDADE_IMPEDE_ENCAMINHAMENTO` (§6 passo 2) — também no 409 `AVALIACAO_MUDOU`. */
export interface ImpedimentoEncaminhamento {
  resultado?: ResultadoElegibilidade | null
  explicacoes: ExplicacaoElegibilidade[]
  politicaId?: string | null
  politicaVersao?: number | null
  politicaTipo?: TipoElegibilidade | null
  hashEntradas?: string | null
  aviso?: string | null
}

/** Linha de `GET /v1/oportunidades/fila/grandes-operacoes` (Q67). */
export interface ReservaGrandesOperacoes {
  id: string
  numero?: number | null
  razaoSocial?: string | null
  nomeFantasia?: string | null
  cnpj?: string | null
  produtoId?: string | null
  produtoCodigo?: string | null
  tipoNecessidade?: string | null
  valorEstimado?: number | null
  status: StatusReserva
  etapaComercial?: EtapaComercial | null
  usuarioResponsavel?: string | null
  consultorNome?: string | null
  rotaOperacao?: RotaOperacao | null
  rotaOrigem?: OrigemRota | null
  /** ISO date-time. */
  rotaDefinidaEm?: string | null
  /** ISO date-time. */
  createdAt?: string | null
}

/** `GET /v1/oportunidades/fila/grandes-operacoes` — `total` é o count da MESMA query (não é PageResponse). */
export interface FilaGrandesOperacoes {
  total: number
  page: number
  size: number
  reservas: ReservaGrandesOperacoes[]
}

export const ROTULO_ORIGEM_ROTA: Readonly<Record<OrigemRota, string>> = {
  AUTOMATICA: 'Automática (política)',
  SOBREPOSTA_ADMIN: 'Definida pelo Administrador',
}

export const ROTULO_ORIGEM_FATURAMENTO: Readonly<Record<OrigemFaturamento, string>> = {
  DECLARADO_CONSULTOR: 'Declarado pelo consultor',
  TRANSCRITO_BACKOFFICE: 'Transcrito pelo back-office',
}

export const ROTULO_MOMENTO_AVALIACAO: Readonly<Record<MomentoAvaliacaoElegibilidade, string>> = {
  CRIACAO: 'Criação da reserva',
  ATUALIZACAO_DADOS: 'Atualização dos dados',
  ALTERACAO_VALOR: 'Alteração do valor',
  TROCA_PRODUTO: 'Troca de produto',
  RECLASSIFICACAO: 'Reclassificação',
  REAVALIACAO: 'Reavaliação',
  SOBREPOSICAO: 'Sobreposição',
  ENCAMINHAMENTO: 'Encaminhamento',
}

export const ROTULO_DECISAO_ELEGIBILIDADE: Readonly<Record<TipoDecisaoElegibilidade, string>> = {
  SOBREPOR_IMPEDIMENTO: 'Impedimento sobreposto',
  ALTERAR_ROTA: 'Rota alterada',
}
